'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, TrendingUp, Zap, BarChart2, MousePointerClick, Percent, Hash } from 'lucide-react';

interface GSCKPIs {
  totalImpressions: number;
  totalClicks: number;
  avgCtr: number;
  avgPosition: number;
  spikeDays: number;
  topQuery: string;
}

interface AboveAverageDaysProps {
  siteUrl: string;
  brandedKeyword: string;
  days: number;
}

export default function AboveAverageDays({ siteUrl, brandedKeyword, days }: AboveAverageDaysProps) {
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<GSCKPIs | null>(null);
  const [aboveAverageDays, setAboveAverageDays] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!siteUrl) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        siteUrl,
        days: String(days),
        brandedKeyword,
      });
      const res = await fetch(`/api/gsc/performance?${params}`);
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setKpis(data.kpis);
        setAboveAverageDays(data.aboveAverageDays ?? []);
      }
    } catch {
      setError('Failed to fetch GSC data');
    } finally {
      setLoading(false);
    }
  }, [siteUrl, brandedKeyword, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!siteUrl) return null;

  if (loading) {
    return (
      <div className="bg-white border border-[#e7e7e7] rounded-[5px] p-5">
        <div className="flex items-center gap-2 mb-4">
          {/* Smarty-style icon wrapper: teal */}
          <div
            className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center"
            style={{ backgroundColor: 'rgb(217, 243, 246)' }}
          >
            <TrendingUp size={15} className="text-[#39364E]" />
          </div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Live GSC Performance</h2>
        </div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-12 rounded-[5px] animate-pulse"
              style={{ backgroundColor: 'rgba(150, 25, 50, 0.06)' }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-[#e7e7e7] rounded-[5px] p-5">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center"
            style={{ backgroundColor: 'rgb(217, 243, 246)' }}
          >
            <TrendingUp size={15} className="text-[#39364E]" />
          </div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Live GSC Performance</h2>
        </div>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!kpis) return null;

  const kpiItems = [
    {
      label: 'Total Impressions',
      value: kpis.totalImpressions.toLocaleString(),
      icon: <BarChart2 size={14} className="text-[#39364E]" />,
      iconBg: 'rgb(217, 243, 246)',
    },
    {
      label: 'Total Clicks',
      value: kpis.totalClicks.toLocaleString(),
      icon: <MousePointerClick size={14} className="text-[#39364E]" />,
      iconBg: 'rgb(224, 240, 253)',
    },
    {
      label: 'Avg CTR',
      value: `${kpis.avgCtr}%`,
      icon: <Percent size={14} className="text-[#39364E]" />,
      iconBg: 'rgb(240, 228, 251)',
    },
    {
      label: 'Avg Position',
      value: `#${kpis.avgPosition}`,
      icon: <Hash size={14} className="text-[#39364E]" />,
      iconBg: 'rgb(217, 243, 246)',
    },
    {
      label: 'Spike Days',
      value: String(kpis.spikeDays),
      icon: <Zap size={14} className="text-white" />,
      iconBg: 'rgb(140, 11, 49)',
    },
    {
      label: 'Top Query',
      value: kpis.topQuery,
      icon: <TrendingUp size={14} className="text-[#39364E]" />,
      iconBg: 'rgb(224, 240, 253)',
    },
  ];

  return (
    <div className="bg-white border border-[#e7e7e7] rounded-[5px] p-5 space-y-5">
      {/* KPI Grid */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-[34px] h-[34px] rounded-[5px] flex items-center justify-center"
            style={{ backgroundColor: 'rgb(217, 243, 246)' }}
          >
            <TrendingUp size={15} className="text-[#39364E]" />
          </div>
          <h2 className="text-[15px] font-600 text-[#39364E]">Live GSC Performance</h2>
          <span className="ml-auto text-[11px] text-[#696969]">
            Last {days} days · branded filter
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {kpiItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-2.5 p-3 rounded-[5px]"
              style={{ backgroundColor: 'rgba(150, 25, 50, 0.04)' }}
            >
              <div
                className="w-7 h-7 rounded-[5px] flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: item.iconBg }}
              >
                {item.icon}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-[#696969] truncate">{item.label}</p>
                <p className="text-sm font-700 text-[#39364E] truncate tabular-nums">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Above Average Days */}
      {aboveAverageDays.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={14} className="text-[#961932]" />
            <h3 className="text-xs font-600 text-[#39364E]">
              Above-Average Traffic Days
              <span
                className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-600"
                style={{
                  backgroundColor: 'rgba(150, 25, 50, 0.08)',
                  color: '#961932',
                  border: '1px solid rgba(150, 25, 50, 0.2)',
                }}
              >
                {aboveAverageDays.length} days
              </span>
            </h3>
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto scrollbar-thin">
            {aboveAverageDays.map((date) => (
              <span
                key={date}
                className="px-2 py-1 rounded-[5px] text-[11px] font-500 tabular-nums"
                style={{
                  backgroundColor: 'rgba(150, 25, 50, 0.06)',
                  color: '#961932',
                  border: '1px solid rgba(150, 25, 50, 0.15)',
                }}
              >
                {date}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
